// Catalog.ID Client — `export-mail-encrypt` consent modal
// ============================================================================
// WHY THIS MODE HAS A DIALOG WHEN `issue-mail-cert` DOES NOT
//
// Certification reads one bag entry, signs, and persists nothing outside the
// keybag; a prompt per 60-day renewal would train click-through. This mode
// hands the mail module on ONE machine a copy of the account's mail
// decryption key, to be sealed under that machine's KEK — the operational
// custody the mail plan (§5.1) adopted because mail decrypts per message and
// cannot afford a keybag hop each time. It happens once per machine, and it
// changes what a compromise of that machine yields. That is a decision, and a
// decision is asked.
//
// WHAT THE DIALOG MUST NAME
//   * the ACCOUNT whose key leaves the bag (the C7 finding: an approval binds
//     to whoever is logged in);
//   * the REQUESTER (hub-attested `mail`) and the machine's own label, so a
//     prompt on the wrong machine, or from the wrong module, is visible;
//   * the WRAP KEY fingerprint — the key the export is sealed to. It is what
//     the signed request committed to and what the mail side must hold to
//     open the export; showing it is what makes a substituted wrap key a thing
//     a careful user could notice, on top of the digest binding.
//   * what it MEANS: this machine can then read mail for the account while
//     the Catalog.ID is locked, and anyone who fully compromises it can read
//     that mail until the key is rotated.
//
// No policy slot ('session' / 'always'): a standing "always export" grant
// would let a compromised mail backend pull the key onto any machine it runs
// on without ever being seen again.

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';

export type MailEncryptExportConsentDecision = 'approve' | 'decline';

export interface MailEncryptExportConsentInput {
    /** The logged-in account whose `mail_encrypt` key is exported. */
    owningCid: string;
    /** Hub-attested requester (pinned to `mail` upstream; rendered, not trusted). */
    requesterId: string;
    /** The requesting machine's label, as the mail module named it. Display only. */
    deviceLabel?: string;
    /** 128-hex SHA-512 of the ephemeral wrap key the export is sealed to. */
    wrapKeyFingerprint: string;
}

export function showMailEncryptExportConsent(
    shell: Shell,
    input: MailEncryptExportConsentInput,
): Promise<MailEncryptExportConsentDecision> {
    return new Promise<MailEncryptExportConsentDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: MailEncryptExportConsentDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });
        // `textContent` (never innerHTML) throughout: the label arrives from
        // another process and is display text, never markup.
        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: 'Give Mail on this machine a copy of your mail decryption key?',
        }));

        const accountGroup = createFieldGroup({ title: 'Exporting from' });
        const accountRow = createFormRow({ label: 'Catalog.ID account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: input.owningCid,
        }));
        accountGroup.body.appendChild(accountRow.element);
        const requesterRow = createFormRow({ label: 'Requested by' });
        requesterRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.deviceLabel
                ? `${input.requesterId} on "${input.deviceLabel}"`
                : input.requesterId,
        }));
        accountGroup.body.appendChild(requesterRow.element);
        container.appendChild(accountGroup.element);

        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            textContent:
                'Mail on this machine will keep a sealed copy of your mail decryption key, so it '
                + 'can receive and read your mail even while your Catalog.ID is locked. Anyone '
                + 'who fully compromises this machine could then read mail sent to you until you '
                + 'rotate the key. The key is sent wrapped to a one-time key only this machine '
                + 'holds; your Catalog.ID keybag remains the master copy.',
        }));

        const keyGroup = createFieldGroup({ title: 'What is exported' });
        const whatRow = createFormRow({ label: 'Key' });
        whatRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: 'Your mail encryption key — every generation, so older mail stays readable',
        }));
        keyGroup.body.appendChild(whatRow.element);
        const wrapRow = createFormRow({ label: 'Sealed to' });
        wrapRow.content.appendChild(createElement('code', {
            textContent: shortFp(input.wrapKeyFingerprint),
            attributes: { title: input.wrapKeyFingerprint },
        }));
        keyGroup.body.appendChild(wrapRow.element);
        container.appendChild(keyGroup.element);

        const actions = createElement('div', { className: 'cs-form-actions' });
        const btnDecline = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Decline',
        }) as HTMLButtonElement;
        btnDecline.addEventListener('click', () => resolveOnce('decline'));
        actions.appendChild(btnDecline);

        const btnApprove = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Allow on this machine',
        }) as HTMLButtonElement;
        btnApprove.addEventListener('click', () => resolveOnce('approve'));
        actions.appendChild(btnApprove);
        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            // A dismissed dialog is a refusal. Never a silent approval, and
            // never a hang: the mail backend is waiting on this promise.
            onClose: () => resolveOnce('decline'),
        });
    });
}

function shortFp(fp: string): string {
    return fp.length <= 24 ? fp : `${fp.slice(0, 8)}…${fp.slice(-8)}`;
}
